import React, { useContext, useEffect } from "react";
import { WidgetComponent } from "../widget.types";
import { EonUiButton, EonUiDatePicker, EonUiDropdown, EonUiDropdownOption, EonUiIcon } from "@eon-ui/eon-ui-components-react";
import "./TeamsAction.widget.scss";
import LoaderComponent from "../../../components/loader-component/LoaderComponent";
import { EonUiDropdownCustomEvent } from "@eon-ui/eon-ui-components";
import { getInsightsReport, getCalls, getInsights } from "@me8eon/ai.tools.truedem_api";
import { getFormattedDateTime } from "@me8eon/time";
import { WidgetMeta } from "@me8eon/ai.tools.utils/src/widget";
import { JwtTokenFn } from "@me8eon/authentication";
import { useAccessTokenFn, useUserData } from "@me8eon/react_authentication";
import { useTranslation } from "@me8eon/translation";
import { useDoAi } from "@me8eon/react_intent";
import { translateKey } from "../../../util/translate";
import { useFeatureFlag } from "@me8eon/featureflags";
import { managedNetworkFFName } from "./managedNetwork.feature.flag";
import { getTimeInSeconds } from "../../../util/date";
import { useFeedback } from "@me8eon/feedback";
import { useMessages } from "@me8eon/sessions_messages";

const delay = (ms: number) =>
    new Promise<void>((resolve) => {
        globalThis.setTimeout(resolve, ms);
    });

export const TEAMS_AGENT_INSIGHTS_KEY = "teamsAgentInsights";
export const TEAMS_AGENT_INSIGHTS_SESSION_FLAG = "teamsAgentInsightsActive";

export type TeamsActionWidgetMeta = WidgetMeta<{
    trueDemApiTokenFn?: JwtTokenFn;
}>

type MeetingInfo = {
    callId: string;
    start: string | number | Date;
    end: string | number | Date;
};

export const TeamsActionWidget: WidgetComponent = (props) => {
    const t = useTranslation();
    const trueDemApiTokenFn = useAccessTokenFn("trueDemApi");
    const [dateValue, setDateValue] = React.useState<string>("");
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [showInsights, setShowInsights] = React.useState<boolean>(false);
    const [showMeetings, setShowMeetings] = React.useState<boolean>(false);
    const [selectedCallId, setSelectedCallId] = React.useState<string>("");
    const [showGetMeetingsCancelMsg, setShowGetMeetingsCancelMsg] = React.useState<boolean>(false);
    const [meetingsDate, setMeetingsDate] = React.useState<string>("");
    const [insightsCallId, setInsightsCallId] = React.useState<string>("");

    const getMeetings = () => {
        setDisabled(true);
        setMeetingsDate(dateValue);
        setShowMeetings(true);
    };

    const identifyIssue = (callId: string) => {
        setInsightsCallId(callId);
        setShowInsights(true);
    };

    const setCallId = (callId: string) => {
        setSelectedCallId(callId);
        setShowInsights(false);
    }

    const changeDate = (date: string) => {
        setDateValue(date);
        setDisabled(false);
        setShowMeetings(false);
        setShowInsights(false);
        setSelectedCallId("");
        setShowGetMeetingsCancelMsg(false);
    };

    const hideInsights = () => setShowInsights(false);

    const cancelGetMeetings = () => {
        setDisabled(true);
        setShowMeetings(false);
        setShowInsights(false);
        setShowGetMeetingsCancelMsg(true);
    };

    return (
        <div className="teams-action-widget">
            <div className="header-section">
                <div className="specify-date-time">{t("chatBot.teamsInsights.specifyDateTime")}</div>
            </div>
            <div className="footer-section">
                <div className="date-time-picker">
                    <EonUiDatePicker
                        onValueChanged={(e) => changeDate(e.target.value)}
                        onDatePickerOpened={(e) => {
                            const parentElement = e.target.parentElement;
                            if (parentElement) {
                                parentElement.style.zIndex = getTimeInSeconds().toString();
                            }
                        }}
                        onDatePickerClosed={(e) => {
                            const parentElement = e.target.parentElement;
                            if (parentElement) {
                                parentElement.style.removeProperty('z-index');
                            }
                        }}
                        value={dateValue}
                        name="date"
                        size="small"
                        label={t("chatBot.teamsInsights.date")}
                        labelOutside={true}
                        maxDaysAfter={0}
                        maxDaysBefore={7}
                        selectToday={true}
                        dateFormat="dd/MM/yyyy"
                    />
                </div>
                <div className="button">
                    <div className="confirm">
                        <EonUiButton text={t("chatBot.teamsInsights.getMeetings")} size="small" onClick={getMeetings} disabled={disabled}></EonUiButton>
                    </div>
                    <div className="cancel">
                        <EonUiButton rank="secondary" text={t("chatBot.teamsInsights.cancel")} size="small" onClick={cancelGetMeetings}></EonUiButton>
                    </div>
                </div>
            </div>
            {showMeetings && <Meetings identifyIssue={identifyIssue} hideInsights={hideInsights} dateValue={meetingsDate} callId={selectedCallId} setCallId={setCallId} trueDemApiTokenFn={trueDemApiTokenFn} />}
            {showInsights && <Insights callId={insightsCallId} trueDemApiTokenFn={trueDemApiTokenFn} />}
            {showGetMeetingsCancelMsg && <div className="cancel-message">{t("chatBot.teamsInsights.getMeetingsCancelMsg")}</div> }
        </div>
    );
}

const Meetings: React.FC<{ identifyIssue: (callId: string) => void; hideInsights: () => void; dateValue: string; callId: string; setCallId: (callId: string) => void; trueDemApiTokenFn: JwtTokenFn }> = ({ identifyIssue, hideInsights, dateValue, callId, setCallId, trueDemApiTokenFn }) => {
    const t = useTranslation();
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [meetings, setMeetings] = React.useState<MeetingInfo[]>([]);
    const [showMeetings, setShowMeetings] = React.useState<boolean>(false);
    const [showIdentifyIssueCancelMsg, setShowIdentifyIssueCancelMsg] = React.useState<boolean>(false);

    const [ messages ] = useMessages();
    const feedback = useFeedback();
    const { company, currentRole } = useUserData();

    const identifyIssueLocal = () => {
        setDisabled(true);
        identifyIssue(callId);
        sendFeedback();
    };

    const sendFeedback = () => {
        const selectedMeeting = meetings.find((meeting) => meeting.callId === callId);
        feedback({
            source: "teams-agent",
            messages: messages,
            query: [...messages].reverse().find(message => message.role === "user")?.content as string || "",
            company: company || "unknown",
            role: currentRole || "unknown",
            meetingId: callId,
            meetingDate: dateValue || "unknown",
            meetingStartTime: selectedMeeting?.start ? String(selectedMeeting.start) : "unknown",
            meetingEndTime: selectedMeeting?.end ? String(selectedMeeting.end) : "unknown",
        });
    };

    const getCallsLocal = async (date: string) => {
        setShowMeetings(false);
        setDisabled(false);
        try {
            const fetchedCalls = (await getCalls(date, trueDemApiTokenFn)) as MeetingInfo[];
            setMeetings(fetchedCalls);
            setCallId(fetchedCalls[0]?.callId ?? "");
            setShowMeetings(true);
        } catch (error) {
            console.error("Error fetching calls:", error);
        }
    };

    useEffect(() => {
        getCallsLocal(dateValue);
    }, [dateValue]);

    const cancelIdentifyIssue = () => {
        setDisabled(true);
        hideInsights();
        setShowIdentifyIssueCancelMsg(true);
    };

    return (
        <div className="bot-header">
            <div className="meeting-list bot-reply-container">
                { !showMeetings && <div className="checking-meetings">
                    <LoaderComponent isLoading={true} isInline size="small" />
                    <div>{`${t("chatBot.teamsInsights.fetchingMeetings")} ${getFormattedDateTime(new Date(dateValue), "dd/MM/yyyy")}`}</div>
                </div>}
                { showMeetings && (meetings.length === 0) && 
                    <>
                        <div className="no-meetings">
                            {t("chatBot.teamsInsights.noMeetings")}
                        </div>
                        <div>{t("chatBot.teamsInsights.windowsOnly")}</div>
                    </>
                }
                { showMeetings && (meetings.length > 0) && (
                <>
                    <div className="meeting-instruction">{t("chatBot.teamsInsights.meetingInstruction")}</div>
                    <div className="meeting-dropdown">
                        <EonUiDropdown
                            onValueChanged={(e: EonUiDropdownCustomEvent<string>) => {
                                setCallId(e.target.value);
                                setDisabled(false);
                                setShowIdentifyIssueCancelMsg(false);
                            }}
                            onDropdownOpened={(e: EonUiDropdownCustomEvent<string>) => {
                                e.target.style.zIndex = getTimeInSeconds().toString();
                            }}
                            onDropdownClosed={(e: EonUiDropdownCustomEvent<string>) => {
                                e.target.style.removeProperty('z-index');
                            }}
                            value={callId}
                            name="time_range"
                            size="small"
                            label={t("chatBot.teamsInsights.meetingList")}
                            showSelectedItems={true}
                            height={meetings.length > 5 ? "200px" : undefined}
                        >
                            {meetings.map((meeting: MeetingInfo) => (
                                <EonUiDropdownOption key={meeting.callId} value={meeting.callId} label={`${getFormattedDateTime(new Date(meeting.start), "H:i", true)} - ${getFormattedDateTime(new Date(meeting.end), "H:i", true)}`} />
                            ))}
                        </EonUiDropdown>
                    </div>
                    <div className="button">
                        <div className="confirm">
                            <EonUiButton text={t("chatBot.teamsInsights.identifyIssue")} size="small" onClick={identifyIssueLocal} disabled={disabled}></EonUiButton>
                        </div>
                        <div className="cancel">
                            <EonUiButton rank="secondary" text={t("chatBot.teamsInsights.cancel")} size="small" onClick={cancelIdentifyIssue}></EonUiButton>
                        </div>
                    </div>
                </>
                )}
                {showIdentifyIssueCancelMsg && <div className="cancel-message">{t("chatBot.teamsInsights.identifyIssueCancelMsg")}</div> }
            </div>
        </div>
    );
};

const Insights: React.FC<{ callId: string; trueDemApiTokenFn: JwtTokenFn }> = ({ callId, trueDemApiTokenFn }) => {
    const t = useTranslation();
    const [initialInsights, setInitialInsights] = React.useState<{ displayName: string; status: string; rootCause: string; recommendation: string }[]>([]);
    const [insights, setInsights] = React.useState<{ displayName: string; status: string; rootCause: string }[]>([]);
    const [recommendation, setRecommendation] = React.useState<string[]>([]);
    const [networkId, setNetworkId] = React.useState<string | null>(null);
    const [bssid, setBssid] = React.useState<string | null>(null);
    const [isManagedNetwork, setIsManagedNetwork] = React.useState<boolean>(false);
    const [showCancelCreateIncidentMsg, setShowCancelCreateIncidentMsg] = React.useState<boolean>(false);
    const [stage, setStage] = React.useState<"fetching" | "detecting" | "detected">("fetching");
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const handleAiQueryIntent = useDoAi();
    const assumeManagedNetworkFF = useFeatureFlag(managedNetworkFFName);
    const incidentPayloadRef = React.useRef<{ callId: string; incidentText: string; recommendation: string } | null>(null);
    const insightsContainerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        insightsContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const getInsightsLocal = async (callId: string) => {
        setStage("fetching");
        try {
            const insights = await getInsights(callId, trueDemApiTokenFn);
            const report = getInsightsReport(insights);
            const {
                callId: reportCallId,
                networkId: reportNetworkId,
                bssid: reportBssid,
                isManagedNetwork: reportIsManagedNetwork,
                sampleCount,
                status,
                insights: initialInsights,
            } = report ?? {
                callId: null,
                networkId: null,
                bssid: null,
                isManagedNetwork: false,
                sampleCount: 0,
                status: null,
                insights: [],
            };

            const isManagedNetwork = assumeManagedNetworkFF ? true : reportIsManagedNetwork;
            const networkId = assumeManagedNetworkFF ? 'SAMPLE-NETWORK-ID' : reportNetworkId;
            const bssid = assumeManagedNetworkFF ? 'SAMPLE-BSSID' : reportBssid;

            if (initialInsights.length) {
                setInitialInsights(initialInsights);
                setNetworkId(networkId);
                setBssid(bssid);
                setIsManagedNetwork(isManagedNetwork);
            } else {
                setStage("detected");
            }
        } catch (error) {
            console.error("Error fetching insights:", error);
            setStage("detected");
        }
    };

    useEffect(() => {
        getInsightsLocal(callId);
    }, [callId]);

    useEffect(() => {
        let isCancelled = false;

        const runSequentialUpdate = async () => {
            let currentInsights = initialInsights.map((insight) => ({
                displayName: insight.displayName,
                status: "checking",
                rootCause: "",
            }));
            setInsights(currentInsights);
            const localRecommendation: string[] = [];

            for (let index = 0; index < initialInsights.length; index += 1) {
                await delay(1000);

                if (isCancelled) {
                    return;
                }

                const completedInsight = initialInsights[index];
                currentInsights = [...currentInsights];
                currentInsights[index] = {
                    ...currentInsights[index],
                    status: completedInsight.status,
                    rootCause: completedInsight.rootCause,
                };
                setInsights(currentInsights);
                if (completedInsight.status === "failed") {
                    localRecommendation.push(translateKey(completedInsight.recommendation));
                    setRecommendation((prev) => [...prev, completedInsight.recommendation]);
                }
            }

            if (!isCancelled) {
                setStage("detected");
                if (isManagedNetwork) {
                    const incidentText = `\nNetwork ID: ${networkId}\nBSSID: ${bssid}`;
                    incidentPayloadRef.current = { callId, incidentText, recommendation: localRecommendation.join("\n") };
                }
            }
        };

        if (initialInsights.length) {
            runSequentialUpdate();
            setStage("detecting");
        }

        return () => {
            isCancelled = true;
        };
    }, [initialInsights]);

    useEffect(() => {
        return () => {
            incidentPayloadRef.current = null;
        };
    }, []);

    const createIncidentAction = () => {
        setDisabled(true);
        if (incidentPayloadRef.current) {
            localStorage.setItem(TEAMS_AGENT_INSIGHTS_KEY, JSON.stringify(incidentPayloadRef.current));
            sessionStorage.setItem(TEAMS_AGENT_INSIGHTS_SESSION_FLAG, "1");
            incidentPayloadRef.current = null;
        }
        handleAiQueryIntent(t(`chatBot.quickActions.actions.createIncident`));
    };

    const cancelCreateIncidentAction = () => {
        setDisabled(true);
        setShowCancelCreateIncidentMsg(true);
        incidentPayloadRef.current = null;
        localStorage.removeItem(TEAMS_AGENT_INSIGHTS_KEY);
        sessionStorage.removeItem(TEAMS_AGENT_INSIGHTS_SESSION_FLAG);
    };

    return (
        <div className="bot-header">
            <div className="insights bot-reply-container" ref={insightsContainerRef}>
                {stage !== "detected" && (
                    <div className="checking-insights">
                        <LoaderComponent isLoading={true} isInline size="small" />
                        <div>{t("chatBot.teamsInsights.detectingIssues")}</div>
                    </div>
                )}
                {stage === "detected" && insights.length > 0 && <div>{t("chatBot.teamsInsights.issueDetectionCompleted")}</div> }
                {insights.length > 0 && (
                    <div className="insights-rows">
                        {insights.map((insight) => (
                            <div className="insight-row" key={t(insight.displayName)}>
                                <div className="insight-name">{t(insight.displayName)}</div>
                                <div className={`insight-status ${insight.status}`}>
                                    <div className="loader">
                                        { insight.status === "checking" && <LoaderComponent isLoading={true} isInline size="small" /> }
                                        { insight.status === "okay" && <div className="status-indicator"><EonUiIcon name="radio_checkbox_tick" style={{ color: "green" }} /> </div> }
                                        { insight.status === "failed" && <div className="status-indicator"><EonUiIcon name="cross" style={{ color: "red" }} /> </div> }
                                        { insight.status === "na" && <div className="status-indicator"><EonUiIcon name="subtract" style={{ color: "gray" }} /> </div> }
                                    </div>
                                    <div className="status-text">{t(`chatBot.teamsInsights.report.${insight.status}`)}</div>
                                </div>
                                <div className="insight-root-cause">{insight.rootCause ? t(insight.rootCause) : null}</div>
                            </div>
                        ))}
                    </div>
                )}
                { stage === "detected" && insights.length > 0 && recommendation.length > 0 && (
                    <div className="recommendation">
                        <div className="recommendation-header">{t("chatBot.teamsInsights.recommendedActions")}</div>
                        <div className="recommendation-body">
                            {recommendation.map((rec) => (
                                <div key={rec}>{t(rec)}</div>
                            ))}
                        </div>
                        { isManagedNetwork && (
                            <div className="create-incident">
                                <div className="confirmation-text">{t("chatBot.teamsInsights.confirmCreateIncident")}</div>
                                <div className="button">
                                    <div className="confirm">
                                        <EonUiButton text={t("yes")} size="small" onClick={createIncidentAction} disabled={disabled}></EonUiButton>
                                    </div>
                                    <div className="cancel">
                                        <EonUiButton rank="secondary" text={t("no")} size="small" onClick={cancelCreateIncidentAction} disabled={disabled}></EonUiButton>
                                    </div>
                                </div>
                                {showCancelCreateIncidentMsg && <div className="cancel-message">{t("chatBot.teamsInsights.createIncidentCancelMsg")}</div> }
                            </div>
                        )}
                    </div>
                )}
                { stage === "detected" && insights.length > 0 && recommendation.length == 0 && (
                    <div className="recommendation">
                        <div className="recommendation-body">{t("chatBot.teamsInsights.noIssueFoundInstruction")}</div>
                    </div>
                )}
                { stage === "detected" && !insights.length && (
                    <div>{t("chatBot.teamsInsights.noInsightsFound")}</div>
                )}
            </div>
        </div>
    );
};